import { useEffect, useId, useRef, useState } from 'react';
import type { PackageListQuery, PackageTab } from '@mypoz/shared';
import { hasActivePackageListQuery, PACKAGE_TABS } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Icon, Select, cn } from '@mypoz/ui';

import { ActiveFilterChip, FiltersPanel, FiltersToggle } from '@/components/ListFilters.js';
import { actionLinkClass } from '@/lib/styles.js';
import { useFormat } from '@/lib/format.js';
import { useSearchBox } from '@/lib/useSearchBox.js';

export interface PackageFilterBarProps {
  query: PackageListQuery;
  /** Every tab, counted by the vendor with the same search and goods type. */
  tabCounts: Readonly<Record<PackageTab, number>>;
  /** The vendor's goods types, already labelled. */
  goodsTypes: readonly { code: string; label: string }[];
  /** `replace` keeps a keystroke out of browser history — see the search box below. */
  onChange: (next: PackageListQuery, options?: { replace?: boolean }) => void;
  onReset: () => void;
}

/** One tab, underlined when active — same recipe as `OrderFilterBar`'s `StatusChip`. */
function PackageTabButton({
  tab,
  count,
  active,
  label,
  onSelect,
}: {
  tab: PackageTab;
  count: number;
  active: boolean;
  label: string;
  onSelect: (tab: PackageTab) => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const fmt = useFormat();

  useEffect(() => {
    if (active) ref.current?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [active]);

  return (
    <button
      ref={ref}
      type="button"
      aria-pressed={active}
      onClick={() => onSelect(tab)}
      className={cn(
        'min-h-11 shrink-0 border-b-2 pb-2.5 text-sm whitespace-nowrap transition-colors lg:min-h-0',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
        active
          ? 'border-brand font-semibold text-ink'
          : 'border-transparent text-muted hover:text-ink',
      )}
    >
      {label}{' '}
      <span className={cn('tabular-nums', active ? 'text-brand' : 'text-muted-soft')}>
        ({fmt.number(count)})
      </span>
    </button>
  );
}

/**
 * Search, the four tabs, and the extra filters folded away behind a Filters button (MYP-128).
 *
 * What a customer reaches for every visit — the search and the tabs — stays on screen. The goods
 * type and "Not on an order yet" open from the Filters button, so the list starts higher; while one
 * is set, a chip under the search says so and removes it in one tap, and the button counts it.
 *
 * Only filters the vendor applies itself are offered (MYP-126): warehouse, sort and storage risk
 * would only filter the page on screen. The overdue-storage warning still shows on each row.
 */
export function PackageFilterBar({
  query,
  tabCounts,
  goodsTypes,
  onChange,
  onReset,
}: PackageFilterBarProps) {
  const { t } = useTranslation();
  const searchId = useId();
  const goodsTypeId = useId();
  const notOrderedHintId = useId();
  const panelId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const [searchText, setSearchText] = useSearchBox(query.search, (next) =>
    onChange({ ...query, search: next }, { replace: true }),
  );

  const active = hasActivePackageListQuery(query);
  const goodsTypeLabel =
    query.goodsType === 'all'
      ? undefined
      : (goodsTypes.find((entry) => entry.code === query.goodsType)?.label ?? query.goodsType);
  const folded = (goodsTypeLabel === undefined ? 0 : 1) + (query.notOrdered ? 1 : 0);

  return (
    <div className="mb-4 grid gap-3">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-lg">
          <label htmlFor={searchId} className="sr-only">
            {t('packages.filters.searchLabel')}
          </label>
          <Icon
            name="search"
            size={20}
            className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-muted"
          />
          <input
            id={searchId}
            type="search"
            value={searchText}
            placeholder={t('packages.filters.searchPlaceholder')}
            onChange={(event) => setSearchText(event.target.value)}
            className={cn(
              // pr-14 clears the 44px clear button that sits at the right edge.
              'min-h-11 w-full rounded-lg bg-surface-alt pr-14 pl-11 text-sm text-ink',
              'transition-colors placeholder:text-muted hover:bg-line/40',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
            )}
          />
          {searchText ? (
            <button
              type="button"
              aria-label={t('packages.filters.clearSearch')}
              onClick={() => setSearchText('')}
              className="absolute top-1/2 right-1 flex size-11 -translate-y-1/2 items-center justify-center rounded-md text-muted transition-colors hover:bg-line/60 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:size-9"
            >
              <Icon name="close" size={18} />
            </button>
          ) : null}
        </div>

        <FiltersToggle
          open={panelOpen}
          activeCount={folded}
          controls={panelId}
          buttonRef={toggleRef}
          onToggle={() => setPanelOpen((open) => !open)}
        />

        {/* Reset appears only once there is something to reset, so it is never a dead control. */}
        {active ? (
          <button type="button" onClick={onReset} className={actionLinkClass('shrink-0')}>
            <Icon name="restart_alt" size={17} />
            <span className="sr-only sm:not-sr-only">{t('orders.filters.reset')}</span>
          </button>
        ) : null}
      </div>

      <FiltersPanel
        id={panelId}
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        returnFocusTo={toggleRef}
      >
        <div className="w-full sm:w-56">
          <label htmlFor={goodsTypeId} className="mb-1.5 block text-sm text-muted">
            {t('packages.filters.goodsType')}
          </label>
          <Select
            id={goodsTypeId}
            value={query.goodsType}
            onChange={(next) => onChange({ ...query, goodsType: next })}
            options={[
              { value: 'all', label: t('packages.filters.goodsTypeAll') },
              ...goodsTypes.map((entry) => ({ value: entry.code, label: entry.label })),
            ]}
          />
        </div>

        {/* The vendor's `validStatus: 1` — packages with no order number yet (MYP-128). */}
        <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm text-ink">
          <input
            type="checkbox"
            checked={query.notOrdered}
            aria-describedby={notOrderedHintId}
            onChange={(event) => onChange({ ...query, notOrdered: event.target.checked })}
            className="size-5 shrink-0 accent-brand"
          />
          <span>
            {t('packages.filters.notOrdered')}
            <span id={notOrderedHintId} className="block text-xs text-muted">
              {t('packages.filters.notOrderedHint')}
            </span>
          </span>
        </label>
      </FiltersPanel>

      {!panelOpen && folded > 0 ? (
        <div className="flex flex-wrap gap-2">
          {goodsTypeLabel === undefined ? null : (
            <ActiveFilterChip
              label={t('packages.filters.goodsType')}
              value={goodsTypeLabel}
              onRemove={() => onChange({ ...query, goodsType: 'all' })}
            />
          )}
          {query.notOrdered ? (
            <ActiveFilterChip
              value={t('packages.filters.notOrdered')}
              onRemove={() => onChange({ ...query, notOrdered: false })}
            />
          ) : null}
        </div>
      ) : null}

      <div
        // The Packages tour points at the status tabs (MYP-83).
        data-tour="packages-tabs"
        className="-mx-1 flex gap-5 overflow-x-auto border-b border-line px-1"
      >
        {PACKAGE_TABS.map((tab) => (
          <PackageTabButton
            key={tab}
            tab={tab}
            count={tabCounts[tab]}
            active={query.tab === tab}
            label={tab === 'all' ? t('orders.filters.all') : t(`packages.tab.${tab}`)}
            onSelect={(next) => onChange({ ...query, tab: next })}
          />
        ))}
      </div>
    </div>
  );
}
